import { FC, useState, useEffect, useMemo } from "react"
import { PageTitle, useIntl } from "@metsooutotec/modes-react-components"
import { useNavigate, useOutletContext, useParams } from "react-router-dom"
import ListView from "components/utils/list-view/list-view"
import { ProvisionEvent } from "features/provisioning/provisions/models/provision-models"
import customerStore from "features/organizations/customers/store/customer-store"
import { provisionEventsBreadcrumbsGenerator } from "../../provisions-utils/provision-events-bread-crumbs"
import { provisionTypeToIcon } from "../../provisions-utils/provision-events-icon"
import { filterProvisionEvents } from "../../provisions-utils/provision-events-filter"
import { observer } from "mobx-react-lite"
import { convertDateToDDMMMYY } from "components/utils/date-formatter-utils"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import { provisionEventsLinkGeneratorSimple } from "../../provisions-utils/provision-events-link-generator"

const ProvisionEventListView: FC = observer(() => {
    const { provisionEvents } = useOutletContext<{ provisionEvents: ProvisionEvent[] }>()

    const navigate = useNavigate()
    const { formatMessage } = useIntl()
    const { customerId, siteId } = useParams()
    const { customerMap, siteMap } = customerStore
    const [tableData, setTableData] = useState<ProvisionEvent[]>([])

    const filteredEvents = useMemo(() => {
        return filterProvisionEvents(provisionEvents, { customerId, siteId })
    }, [provisionEvents, customerId, siteId])

    useEffect(() => {
        setTableData(filteredEvents)
    }, [filteredEvents])

    const template = [
        {
            headingKey: "provisionEvents.headings.eventDate",
            cellFunc: (s: ProvisionEvent) => convertDateToDDMMMYY(s.eventDate) ?? "",
            isSearchable: true,
        },
        {
            headingKey: "provisionEvents.headings.provision",
            cellFunc: (s: ProvisionEvent) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    {provisionTypeToIcon(s.type)}
                    {s.type}
                </span>
            ),
            isSearchable: true,
        },
        {
            headingKey: "provisionEvents.headings.description",
            cellFunc: (s: ProvisionEvent) => s?.description ?? "",
            isSearchable: true,
        },
    ]

    if (!customerId) {
        template.push({
            headingKey: "provisionEvents.headings.customer",
            cellFunc: (s: ProvisionEvent) => s?.customer?.name ?? "",
            isSearchable: true,
        })
    }

    if (!siteId) {
        template.push({
            headingKey: "provisionEvents.headings.site",
            cellFunc: (s: ProvisionEvent) => s?.site?.name ?? "",
            isSearchable: true,
        })
    }

    template.push({
        headingKey: "provisionEvents.headings.asset",
        cellFunc: (s: ProvisionEvent) => s?.asset?.name ?? "",
        isSearchable: true,
    })

    const tableContainerStyles = {
        ...itemViewDetailsContainerStyles,
        height: "91%",
    }

    const breadcrumbsPath = provisionEventsBreadcrumbsGenerator(
        customerId ? customerMap[customerId] : undefined,
        siteId ? siteMap[siteId] : undefined,
        undefined,
        undefined,
        formatMessage
    )

    return (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={formatMessage("provisionEvents.title")} />
            <div style={tableContainerStyles}>
                {
                    <ListView
                        noneYetKey={"provisionEvents.noneYet"}
                        template={template}
                        data={tableData}
                        onClickRow={(a: ProvisionEvent) => {
                            const navStub = provisionEventsLinkGeneratorSimple(a.customer.id, a.site.id, a.asset.id)
                            navigate(`${navStub}/event/${a.id}`)
                        }}
                    />
                }
            </div>
        </>
    )
})

export default ProvisionEventListView
